/* ==========================================================================
   PC Brake Pad Life Calculator
   Self-contained. Loaded only on the brake pad life calculator page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-bpl]');
  if (!root) return;

  /* ====================================================================
     Constants

     Pad thickness figures are friction material only, excluding the steel
     backing plate. 3mm is the widely published replacement threshold; 2mm
     is where most manufacturers consider the pad fully worn.

     Base wear rates are mm per 1,000 miles for FRONT pads on a midsize
     passenger car, derived from the commonly cited service life ranges:
     ~30k miles stop-and-go, ~45k mixed, ~70k highway, over the 7mm of
     usable material between a 10mm new pad and the 3mm threshold.
     ==================================================================== */
  var MIN_MM      = 3.0;   /* replacement threshold */
  var CRITICAL_MM = 2.0;   /* effectively worn out */
  var MM_PER_INCH = 25.4;

  var NEW_MM = { car: 10, suv: 11, truck: 12 };

  var WEAR_BASE = { city: 0.233, mixed: 0.156, highway: 0.100 };

  var VEHICLE_MULT = { car: 1.00, suv: 1.18, truck: 1.35 };

  /* Front pads do roughly 70-75% of the braking work. */
  var AXLE_MULT = { front: 1.00, rear: 0.55 };

  var TOW_MULT = 1.30;

  var MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.

     input: {
       thickness:    Number,  currentUnit: 'mm' | 'in',
       newThickness: Number|null (mm, optional override),
       vehicle: 'car'|'suv'|'truck',
       axle:    'front'|'rear',
       driving: 'city'|'mixed'|'highway',
       towing:  Boolean,
       annualMiles: Number
     }
     ==================================================================== */
  function compute(input) {
    var errors = [];

    var currentMm = input.currentUnit === 'in'
      ? input.thickness * MM_PER_INCH
      : input.thickness;

    var newMm = input.newThickness && input.newThickness > 0
      ? input.newThickness
      : NEW_MM[input.vehicle];

    if (!isFinite(currentMm) || currentMm <= 0) {
      errors.push('Enter the current pad thickness.');
    }
    if (!isFinite(input.annualMiles) || input.annualMiles <= 0) {
      errors.push('Enter how many miles you drive per year.');
    }
    if (errors.length) return { ok: false, errors: errors };

    var warnings = [];
    if (currentMm > newMm) {
      warnings.push('That is thicker than a typical new pad for this vehicle type ('
        + newMm + 'mm). Check that you measured the friction material only, '
        + 'not the steel backing plate.');
      newMm = currentMm;
    }

    var wearRate = WEAR_BASE[input.driving]
                 * VEHICLE_MULT[input.vehicle]
                 * AXLE_MULT[input.axle]
                 * (input.towing ? TOW_MULT : 1);

    var usableRemaining = Math.max(0, currentMm - MIN_MM);
    var usableTotal     = Math.max(0.1, newMm - MIN_MM);

    var milesRemaining  = (usableRemaining / wearRate) * 1000;
    var monthsRemaining = (milesRemaining / input.annualMiles) * 12;

    var pctRemaining = Math.max(0, Math.min(100,
      (usableRemaining / usableTotal) * 100));

    var status = statusFor(currentMm);

    var replacementDate = null;
    if (usableRemaining > 0 && isFinite(monthsRemaining)) {
      replacementDate = addMonths(new Date(), monthsRemaining);
    }

    return {
      ok: true,
      errors: [],
      warnings: warnings,
      currentMm: currentMm,
      newMm: newMm,
      wearRatePer1k: wearRate,
      milesRemaining: milesRemaining,
      monthsRemaining: monthsRemaining,
      pctRemaining: pctRemaining,
      replacementDate: replacementDate,
      status: status
    };
  }

  function statusFor(mm) {
    if (mm <= CRITICAL_MM) {
      return {
        key: 'critical',
        label: 'Worn out. Do not drive',
        note: 'Almost no friction material left. Get it inspected before you '
            + 'drive it again.'
      };
    }
    if (mm <= MIN_MM) {
      return {
        key: 'now',
        label: 'Replace now',
        note: 'At or below 3mm. Keep driving on these and you will be buying '
            + 'rotors too.'
      };
    }
    if (mm <= 4) {
      return {
        key: 'soon',
        label: 'Replace soon',
        note: 'Under 4mm. Book the job in the next few weeks.'
      };
    }
    if (mm <= 6) {
      return {
        key: 'monitor',
        label: 'Monitor',
        note: 'Still serviceable. Measure again at your next tire rotation.'
      };
    }
    return {
      key: 'good',
      label: 'Plenty of life left',
      note: 'Most of the usable material is still there.'
    };
  }

  /* Advance a date by a fractional number of months. */
  function addMonths(date, months) {
    var d = new Date(date.getTime());
    var whole = Math.floor(months);
    var frac  = months - whole;
    d.setMonth(d.getMonth() + whole);
    if (frac > 0) d.setDate(d.getDate() + Math.round(frac * 30.44));
    return d;
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  /* Round to the nearest 100 above 1,000 - this is an estimate, and a
     figure like "44,900 miles" reads as more precise than it deserves. */
  function fmtMiles(n) {
    if (!isFinite(n)) return '-';
    var rounded = n >= 1000 ? Math.round(n / 100) * 100 : Math.round(n);
    return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fmtDuration(months) {
    if (!isFinite(months) || months <= 0) return '-';
    if (months < 1)  return 'Less than a month';
    if (months < 24) {
      var m = Math.round(months);
      return m + (m === 1 ? ' month' : ' months');
    }
    var years = months / 12;
    return (Math.round(years * 10) / 10) + ' years';
  }

  function fmtDate(d) {
    if (!d) return '-';
    return MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var els = {
    thickness:   root.querySelector('[data-bpl-thickness]'),
    unit:        root.querySelector('[data-bpl-unit]'),
    vehicle:     root.querySelector('[data-bpl-vehicle]'),
    axle:        root.querySelector('[data-bpl-axle]'),
    driving:     root.querySelector('[data-bpl-driving]'),
    towing:      root.querySelector('[data-bpl-towing]'),
    annualMiles: root.querySelector('[data-bpl-miles]'),
    error:       root.querySelector('[data-bpl-error]'),

    status:      root.querySelector('[data-bpl-status]'),
    statusValue: root.querySelector('[data-bpl-status-value]'),
    statusNote:  root.querySelector('[data-bpl-status-note]'),
    milesValue:  root.querySelector('[data-bpl-miles-value]'),
    milesSub:    root.querySelector('[data-bpl-miles-sub]'),
    dateValue:   root.querySelector('[data-bpl-date-value]'),
    dateSub:     root.querySelector('[data-bpl-date-sub]'),
    barFill:     root.querySelector('[data-bpl-bar]'),
    barPct:      root.querySelector('[data-bpl-bar-pct]'),
    warnings:    root.querySelector('[data-bpl-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function readInput() {
    return {
      thickness:   parseFloat(els.thickness.value),
      currentUnit: els.unit.value,
      newThickness: null,
      vehicle:     els.vehicle.value,
      axle:        els.axle.value,
      driving:     els.driving.value,
      towing:      els.towing.checked,
      annualMiles: parseFloat(els.annualMiles.value)
    };
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      els.thickness.classList.toggle('is-invalid',
        !isFinite(input.thickness) || input.thickness <= 0);
      clearResult(result.errors[0]);
      return;
    }

    els.error.classList.remove('is-shown');
    els.thickness.classList.remove('is-invalid');

    /* Status banner */
    STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
    els.status.classList.add('is-' + result.status.key);
    els.statusValue.textContent = result.status.label;
    els.statusNote.textContent  = result.status.note;

    /* Remaining life */
    if (result.milesRemaining <= 0) {
      els.milesValue.textContent = '0 miles';
      els.milesValue.classList.add('is-muted');
      els.milesSub.textContent = 'At or below the replacement threshold.';
      els.dateValue.textContent = 'Now';
      els.dateValue.classList.add('is-muted');
      els.dateSub.textContent = '';
    } else {
      els.milesValue.classList.remove('is-muted');
      els.dateValue.classList.remove('is-muted');
      els.milesValue.textContent = fmtMiles(result.milesRemaining) + ' miles';
      els.milesSub.textContent =
        'About ' + fmtDuration(result.monthsRemaining) + ' at '
        + fmtMiles(input.annualMiles) + ' miles per year.';
      els.dateValue.textContent = fmtDate(result.replacementDate);
      els.dateSub.textContent =
        'Estimated wear rate ' + result.wearRatePer1k.toFixed(2)
        + 'mm per 1,000 miles.';
    }

    /* Wear bar */
    els.barFill.style.width = result.pctRemaining.toFixed(1) + '%';
    STATUS_CLASSES.forEach(function (c) { els.barFill.classList.remove(c); });
    els.barFill.classList.add('is-' + result.status.key);
    els.barPct.textContent = Math.round(result.pctRemaining) + '% of usable material left';

    /* Warnings */
    if (result.warnings.length) {
      els.warnings.textContent = result.warnings[0];
      els.warnings.style.display = '';
    } else {
      els.warnings.style.display = 'none';
    }

    writePermalink(input);
  }

  /* Blank the result panel when the inputs are incomplete. Leaving the
     previous figures on screen next to a validation error would show a
     mileage that does not correspond to what the user typed - not
     acceptable on a brake tool. */
  function clearResult(message) {
    STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
    els.statusValue.textContent = 'Waiting for your measurements';
    els.statusNote.textContent  = message || '';
    els.milesValue.textContent = '-';
    els.dateValue.textContent  = '-';
    els.milesValue.classList.add('is-muted');
    els.dateValue.classList.add('is-muted');
    els.milesSub.textContent = '';
    els.dateSub.textContent  = '';
    els.barFill.style.width = '0%';
    STATUS_CLASSES.forEach(function (c) { els.barFill.classList.remove(c); });
    els.barPct.textContent = '';
    els.warnings.style.display = 'none';
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = '?t=' + encodeURIComponent(input.thickness)
          + '&u=' + encodeURIComponent(input.currentUnit)
          + '&v=' + encodeURIComponent(input.vehicle)
          + '&a=' + encodeURIComponent(input.axle)
          + '&d=' + encodeURIComponent(input.driving)
          + '&m=' + encodeURIComponent(input.annualMiles)
          + (input.towing ? '&tow=1' : '');
    try { window.history.replaceState(null, '', window.location.pathname + q); } catch (e) {}
  }

  function applyPermalink() {
    var p = new URLSearchParams(window.location.search);
    if (!p.get('t')) return;
    if (p.get('t')) els.thickness.value   = p.get('t');
    if (p.get('u')) els.unit.value        = p.get('u');
    if (p.get('v')) els.vehicle.value     = p.get('v');
    if (p.get('a')) els.axle.value        = p.get('a');
    if (p.get('d')) els.driving.value     = p.get('d');
    if (p.get('m')) els.annualMiles.value = p.get('m');
    els.towing.checked = p.get('tow') === '1';
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-bpl-form]')) render();
    });
  });

  if (window.URLSearchParams) applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PCBrakePadLife = { compute: compute };
})();
